// Свързаните маси на платното — чиста логика без React (тестове:
// tableLinks.test.ts): кои маси на една резервация на няколко маси се
// свързват с линия в момента от времевата линия. Пресмята се наново при
// всяко местене на линията — по кешираните състояния на снимката
// (`createFloorSnapshot`), без собствено обхождане на резервациите.

import type { Reservation } from "../../../api/reservations";
import type { TableState } from "./tableStatus";

/**
 * Видът на връзката:
 * - selected — избраната резервация (клик на маса или карта): силна, над
 *   всички останали, през всичките ѝ маси в региона;
 * - occupied — настанена: масите ѝ са „Заета";
 * - reserved — предстояща в часа си: масите ѝ са „Резервирана";
 * - upcoming — следващата предстояща, чийто час стои в чипа над свободните
 *   маси („18:30" над 1, 2 и 3).
 */
export type TableLinkTone = "selected" | "occupied" | "reserved" | "upcoming";

/** Масите на една резервация, които се свързват (поне две). */
export interface TableLinkGroup<Table extends { id: number }> {
  key: string;
  tone: TableLinkTone;
  tables: Table[];
}

/**
 * Резервацията, която масата ПОКАЗВА в момента — с плота си (заета,
 * резервирана) или с чипа над нея (следващата предстояща). Заета маса без
 * резервация (сметка от Маркиране) не показва резервация.
 */
const shownReservationOf = (
  state: TableState,
): { reservation: Reservation; tone: Exclude<TableLinkTone, "selected"> } | null => {
  if (state.kind === "occupied") {
    return state.reservation ? { reservation: state.reservation, tone: "occupied" } : null;
  }
  if (state.kind === "reserved") return { reservation: state.reservation, tone: "reserved" };
  return state.next ? { reservation: state.next, tone: "upcoming" } : null;
};

/**
 * Връзките в момента: всяка резервация на няколко маси, която поне две маси
 * от региона показват, свързва точно тях (маса, която в момента показва
 * друго — друга резервация, сметка без резервация — остава извън линията).
 * Избраната резервация е отделна, силна връзка през всичките си маси в
 * региона; масите ѝ не влизат в постоянните връзки — иначе две резервации
 * на същите маси (избраната за 20:00 и настанената сега) биха се слели в
 * една линия. Избраната е последна — рисува се най-отгоре.
 */
export const tableLinkGroupsOf = <Table extends { id: number }>({
  tables,
  stateOf,
  selectedTableIds,
}: {
  // Масите на региона.
  tables: Table[];
  // Състоянието на масата в момента (кешираното на снимката).
  stateOf: (table: Table) => TableState;
  // Масите на избраната резервация (празно — няма избрана).
  selectedTableIds: ReadonlySet<number>;
}): TableLinkGroup<Table>[] => {
  const selectedTables = tables.filter((table) => selectedTableIds.has(table.id));
  const hasSelectedLink = selectedTables.length >= 2;
  const groups = new Map<string, TableLinkGroup<Table>>();
  for (const table of tables) {
    if (hasSelectedLink && selectedTableIds.has(table.id)) continue;
    const shown = shownReservationOf(stateOf(table));
    if (!shown || shown.reservation.tableIds.length < 2) continue;
    const key = `${shown.tone}-${shown.reservation.id}`;
    const group = groups.get(key) ?? { key, tone: shown.tone, tables: [] };
    group.tables.push(table);
    groups.set(key, group);
  }
  const persistent = [...groups.values()].filter((group) => group.tables.length >= 2);
  return hasSelectedLink
    ? [...persistent, { key: "selected", tone: "selected", tables: selectedTables }]
    : persistent;
};
